import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Send,
  FolderPlus,
  X,
  Sparkles,
  Plus,
  MessageSquare,
  PenLine,
  ClipboardList,
  Lock,
  Crosshair,
} from "lucide-react";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import { Skeleton } from "../components/common/Skeleton";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import toast from "react-hot-toast";

import { aiService } from "../services/aiService";
import { Button } from "../components/ui/button";
import { AddTestCasesToProjectModal } from "../components/AddTestCasesToProjectModal";
import { cn } from "../lib/utils";

/** Quick-action chips: backend content_type_id + starter prompt when input is empty */
const CHIPS = [
  {
    content_type_id: "generate_test_case",
    label: "Generate Test Case",
    Icon: PenLine,
  },
  {
    content_type_id: "generate_test_plan",
    label: "Generate Test Plan",
    Icon: ClipboardList,
  },
  {
    content_type_id: "generate_security_test_cases",
    label: "Generate Security Test Cases",
    Icon: Lock,
  },
  {
    content_type_id: "suggest_selector",
    label: "Suggest selector / locator for an element",
    Icon: Crosshair,
    
  },
];

function normalizeApiMessages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m) => {
      const roleRaw = (m.role || m.sender || m.from || "")
        .toString()
        .toLowerCase();
      let role = "user";
      if (roleRaw === "assistant" || roleRaw === "ai" || roleRaw === "bot")
        role = "assistant";
      if (roleRaw === "system") role = "system";
      const content =
        m.content ??
        m.text ??
        m.message ??
        m.body ??
        (typeof m === "string" ? m : "");
      return { role, content: String(content || "").trim() };
    })
    .filter((m) => m.content && m.role !== "system");
}

/* ── shared input box ─────────────────────────────────────────────────────── */
function InputBox({
  input,
  setInput,
  isMultiLine,
  textareaRef,
  autoResize,
  send,
  isSending,
  selectedChip,
  setSelectedChip,
  onChipClick,
}) {
  return (
    <div className="w-full">
      <div
        className={cn(
          "border border-gray-200 bg-white shadow-sm px-4 py-2 flex gap-3 transition-all duration-150",
          isMultiLine ? "rounded-2xl items-end" : "rounded-full items-center",
        )}
      >
        <button
          type="button"
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 text-xl leading-none transition-colors"
          title="Attach (coming soon)"
        >
          +
        </button>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            autoResize();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask anything…"
          rows={1}
          style={{
            resize: "none",
            overflow: "auto",
            lineHeight: "24px",
            minHeight: "24px",
            maxHeight: "120px",
          }}
          className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={send}
          disabled={isSending || !input.trim()}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {isSending ? (
            <InlineLoadSkeleton className="h-4 w-4" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {CHIPS.map((chip) => {
          const isSelected = selectedChip === chip.content_type_id;
          const ChipIcon = chip.Icon;
          return (
            <button
              key={chip.content_type_id}
              type="button"
              onClick={() => onChipClick(chip)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors",
                isSelected
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400 hover:bg-gray-50",
              )}
            >
              <ChipIcon className="w-3.5 h-3.5 shrink-0" />
              <span>{chip.label}</span>
              {isSelected && (
                <span
                  role="button"
                  tabIndex={0}
                  className="inline-flex ml-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedChip(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedChip(null);
                    }
                  }}
                >
                  <X className="w-3.5 h-3.5 opacity-80" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────────── */
export default function AIAssistantPage() {
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
  const [testCasesToAdd, setTestCasesToAdd] = useState([]);
  const [selectedChip, setSelectedChip] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [isMultiLine, setIsMultiLine] = useState(false);

  const [prevChats, setPrevChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [activeChatIdSidebar, setActiveChatIdSidebar] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  /** Draft input + chip per chat (and 'new' for unsaved session) */
  const draftsRef = useRef({});

  const containerRef = useRef(null);
  const textareaRef = useRef(null);

  const lastAssistantMessage = useMemo(
    () =>
      [...messages].reverse().find((m) => m.role === "assistant")?.content ??
      "",
    [messages],
  );

  const systemPrompt = useMemo(
    () =>
      "You are a helpful QA assistant for TestNimbus. Be concise, practical, and output structured steps when helpful.",
    [],
  );

  const draftKey = chatId || "__new__";

  const saveCurrentDraft = useCallback(() => {
    draftsRef.current[draftKey] = { input, selectedChip };
  }, [draftKey, input, selectedChip]);

  const restoreDraft = useCallback((key) => {
    const d = draftsRef.current[key];
    setInput(d?.input ?? "");
    setSelectedChip(d?.selectedChip ?? null);
    if (textareaRef.current) {
      textareaRef.current.style.height = "24px";
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.style.height = "auto";
          el.style.height = Math.min(el.scrollHeight, 120) + "px";
        }
      });
    }
  }, []);

  const loadChatsList = useCallback(async () => {
    try {
      const res = await aiService.chats({});
      const list = res?.data?.chats ?? res?.chats ?? res?.data ?? [];
      setPrevChats(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Failed to load chats", e);
      setPrevChats([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setChatsLoading(true);
        await loadChatsList();
      } finally {
        setChatsLoading(false);
      }
    })();
  }, [loadChatsList]);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
    setIsMultiLine(Math.round(el.scrollHeight / 24) > 1);
  }, []);

  const loadThreadForChat = useCallback(
    async (id) => {
      if (!id) {
        setMessages([]);
        return;
      }
      setThreadLoading(true);
      try {
        const res = await aiService.getChatMessages(id);
        const raw =
          res?.data?.messages ??
          res?.messages ??
          res?.data?.data ??
          res?.data ??
          [];
        setMessages(normalizeApiMessages(raw));
        setTimeout(scrollToBottom, 0);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || "Could not load this chat");
        setMessages([]);
      } finally {
        setThreadLoading(false);
      }
    },
    [scrollToBottom],
  );

  const selectSidebarChat = useCallback(
    async (id) => {
      if (!id) return;
      if (id === chatId) return;
      if (!chatId && messages.length > 0) {
        const ok = window.confirm(
          "You have messages in a new chat that may not be saved yet. Switch to this chat anyway?",
        );
        if (!ok) return;
      }
      saveCurrentDraft();
      setChatId(id);
      setActiveChatIdSidebar(id);
      await loadThreadForChat(id);
      restoreDraft(id);
    },
    [
      chatId,
      messages.length,
      loadThreadForChat,
      restoreDraft,
      saveCurrentDraft,
    ],
  );

  const startNewChat = useCallback(() => {
    saveCurrentDraft();
    setMessages([]);
    setChatId(null);
    setActiveChatIdSidebar(null);
    setInput("");
    setSelectedChip(null);
    setIsMultiLine(false);
    if (textareaRef.current) textareaRef.current.style.height = "24px";
    restoreDraft("__new__");
  }, [restoreDraft, saveCurrentDraft]);

  const onChipClick = useCallback(
    (chip) => {
      if (selectedChip === chip.content_type_id) {
        setSelectedChip(null);
        return;
      }
      setSelectedChip(chip.content_type_id);
      // setInput((prev) => (prev.trim() ? prev : chip.starterPrompt));
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
        autoResize();
      });
    },
    [autoResize, selectedChip],
  );

  const send = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    let activeChatId = chatId;
    if (!activeChatId) {
      try {
        const newChat = await aiService.new({});
        activeChatId =
          newChat?.chat_id ?? newChat?.data?.chat_id ?? newChat?.id ?? null;
        if (activeChatId) {
          setChatId(activeChatId);
          setActiveChatIdSidebar(activeChatId);
          const title = text.slice(0, 56) + (text.length > 56 ? "…" : "");
          setPrevChats((prev) => {
            const pid = activeChatId;
            if (prev.some((c) => (c.chat_id || c.id) === pid)) return prev;
            return [
              { chat_id: pid, id: pid, title, first_message: text },
              ...prev,
            ];
          });
        }
      } catch (e) {
        console.error("Failed to create chat session", e);
        toast.error("Could not start a chat session");
        return;
      }
    }

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    draftsRef.current[draftKey] = { input: "", selectedChip };
    setIsSending(true);
    if (textareaRef.current) textareaRef.current.style.height = "24px";
    setIsMultiLine(false);
    setTimeout(scrollToBottom, 0);

    try {
      const resp = await aiService.chat({
        messages: next,
        systemPrompt,
        content_type_id: selectedChip || undefined,
        chat_id: activeChatId || undefined,
      });
      const reply = resp?.reply ?? resp?.message ?? resp?.data?.reply ?? "";
      if (!reply) throw new Error("Empty response from AI service");
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      setTimeout(scrollToBottom, 0);

      await loadChatsList();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message || e.message || "Failed to chat with AI",
      );
    } finally {
      setIsSending(false);
    }
  };

  const hasMessages = messages.length > 0;
  /** Hero empty state only when not in a persisted thread */
  const showLandingHero = !threadLoading && !chatId && !hasMessages;
  const inputProps = {
    input,
    setInput,
    isMultiLine,
    textareaRef,
    autoResize,
    send,
    isSending,
    selectedChip,
    setSelectedChip,
    onChipClick,
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* ── LEFT SIDEBAR ── */}
      <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-sm font-semibold text-gray-700">Chats</span>
          <button
            type="button"
            onClick={startNewChat}
            className="w-7 h-7 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
            title="New chat"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {chatsLoading ? (
            <div className="space-y-2 px-3 py-4">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-[83%]" />
            </div>
          ) : prevChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-2">
              <MessageSquare className="w-8 h-8 text-gray-300" />
              <p className="text-xs text-gray-400">No chats yet</p>
              <p className="text-[11px] text-gray-400 px-2">
                Send a message to create your first chat, or use a quick action
                below.
              </p>
            </div>
          ) : (
            prevChats.map((chat) => {
              const id = chat.chat_id || chat.id;
              const title =
                chat.title ||
                chat.name ||
                chat.first_message ||
                `Chat ${id?.slice?.(0, 8) || ""}`;
              const isActive =
                activeChatIdSidebar === id || (chatId === id && id != null);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => selectSidebarChat(id)}
                  className={cn(
                    "w-full text-left px-4 py-2.5 text-sm truncate transition-colors",
                    isActive
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100",
                  )}
                  title={title}
                >
                  {title}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── MAIN AREA ── */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            <span className="text-base font-semibold text-gray-900">
              AI Assistant
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!chatId}
            onClick={() => setShowAddToProjectModal(true)}
            className="shrink-0 h-8 text-xs"
          >
            <FolderPlus className="w-3.5 h-3.5 mr-1.5" />
            Add to project
          </Button>
        </div>

        {threadLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="py-8"><TableSkeleton rows={4} columns={2} /></div>
          </div>
        ) : showLandingHero ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 pb-10 gap-8">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-7 h-7 text-indigo-500" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">
                How can I help you?
              </h2>
              <p className="text-sm text-gray-500 max-w-sm">
                Generate test cases, write test plans, suggest selectors, or ask
                anything QA-related.
              </p>
            </div>
            <div className="w-full max-w-2xl">
              <InputBox {...inputProps} />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 px-6 pb-4">
            <div
              ref={containerRef}
              className="flex-1 overflow-y-auto py-4 space-y-3 min-h-0"
            >
              {messages.map((m, idx) => {
                const isUser = m.role === "user";
                return (
                  <div
                    key={`${idx}-${m.content?.slice?.(0, 12)}`}
                    className={cn(
                      "flex",
                      isUser ? "justify-end" : "justify-start",
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
                        isUser
                          ? "bg-indigo-600 text-white"
                          : "bg-white border border-gray-200 text-gray-900 shadow-sm",
                      )}
                    >
                      {m.content}
                    </div>
                  </div>
                );
              })}
              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 text-gray-700 rounded-2xl px-4 py-3 text-sm flex items-center gap-2 shadow-sm">
                    <InlineLoadSkeleton className="h-4 w-4" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              )}
            </div>
            <div className="pt-3 border-t border-gray-100 flex-shrink-0">
              <InputBox {...inputProps} />
            </div>
          </div>
        )}
      </div>

      <AddTestCasesToProjectModal
        isOpen={showAddToProjectModal}
        onClose={() => setShowAddToProjectModal(false)}
        testCasesToAdd={testCasesToAdd}
        lastAssistantMessage={lastAssistantMessage}
        onImportFromMessage={setTestCasesToAdd}
        onSaved={() => {
          setShowAddToProjectModal(false);
          setTestCasesToAdd([]);
          toast.success("Test cases added to project");
        }}
      />
    </div>
  );
}
